/**
 * How a workflow row links to an oraFix campaign (spec 2026-09-28 §7.2 step 2,
 * §11.4). A campaign chunk is an `orafix_run_v1` row whose
 * `metadata.launch_inputs.campaign_instance_id` names its campaign: the register
 * activity writes it at insert, and every push of the chunk re-sends it inside
 * `launch_inputs`.
 *
 * Shared by ora (CampaignBanner) and the Outlook add-in (orchestratorApi's
 * listInstances, which keeps campaigns out of the add-in) so both surfaces read
 * the link the same way (ui/CLAUDE.md parity rule). No imports: the add-in
 * bundles this file, and `~/features/workflows/orafixCampaign`'s import chain
 * reaches a `.server` module.
 */

/** Same value as `ORAFIX_CAMPAIGN_WORKFLOW_ID` in `~/features/workflows/orafixCampaign`
 *  (orafixCampaignLink.test.ts pins them equal). */
export const CAMPAIGN_WORKFLOW_ID = "orafix_campaign_v1";

/** The campaign a run belongs to (`launch_inputs.campaign_instance_id`), or null. */
export function campaignIdOf(launchInputs: unknown): string | null {
  if (launchInputs === null || typeof launchInputs !== "object") return null;
  const id = (launchInputs as Record<string, unknown>).campaign_instance_id;
  return typeof id === "string" && id !== "" ? id : null;
}

/** True for a campaign row and for each of its chunk rows. W8's register insert
 *  writes the link at top level (`metadata.campaign_instance_id`, the one W10's
 *  cancel hook keys on) and inside `launch_inputs` (re-sent by every chunk push);
 *  either one marks a chunk. */
export function isCampaignRow(row: { workflow_id: string; metadata?: unknown }): boolean {
  if (row.workflow_id === CAMPAIGN_WORKFLOW_ID) return true;
  const metadata = row.metadata;
  if (metadata === null || typeof metadata !== "object") return false;
  const m = metadata as Record<string, unknown>;
  return campaignIdOf(m) !== null || campaignIdOf(m.launch_inputs) !== null;
}
